import { useContext, useState } from "react";
import {
  RestrictToVerticalAxis,
  SnapModifier,
} from "@dnd-kit/abstract/modifiers";
import { pointerIntersection } from "@dnd-kit/collision";
import { useDragDropMonitor } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { JointBillContext } from "@features/JointBillList/utils";
import { FeatureFlag } from "@components/FeatureFlag/FeatureFlag";
import { IconButton } from "@components/IconButton/IconButton";
import { ChevronIcon, SixDotsIcon } from "@components/icons";
import { isNullOrUndefined } from "@utils/common";

interface IJointBillTableLayoutProps {
  id: number;
  orderId: number;
  headerNameCell?: JSX.Element | string;
  headerStatusCell?: JSX.Element | string;
  headerTotalCell?: JSX.Element | string;
  footerTotalCell?: JSX.Element | null;
  emptyStateCell?: JSX.Element | null;
  mobileTotals?: JSX.Element | null;
  headerNotification?: JSX.Element | null;
  isEmpty?: boolean;
  isBillDefaultOpen?: boolean;
  canBePaid?: boolean;
  paidAmount?: number;
}

export const JointBillTableLayout: React.FC<
  React.PropsWithChildren<IJointBillTableLayoutProps>
> = ({
  id,
  orderId,
  headerNameCell,
  headerStatusCell,
  headerTotalCell,
  footerTotalCell,
  mobileTotals,
  headerNotification,
  isEmpty,
  isBillDefaultOpen = true,
  emptyStateCell,
  children,
  canBePaid,
}) => {
  const {
    ref,
    handleRef,
    isDragging: isCurrentDragging,
  } = useSortable({
    id: id,
    index: orderId,
    modifiers: [
      RestrictToVerticalAxis,
      SnapModifier.configure({
        size: {
          x: 1,
          y: 20,
        },
      }),
    ],
    collisionDetector: pointerIntersection,
  });

  const [isDragging, setIsDragging] = useState(false);

  useDragDropMonitor({
    onBeforeDragStart: () => {
      window.scrollTo(0, 0);
      flushSync(() => setIsDragging(true));
    },
    onDragEnd: () => setIsDragging(false),
  });

  const { isSummaryMode } = useContext(JointBillContext);
  const [isBillOpen, setIsBillOpen] = useState(isBillDefaultOpen);

  const toggleBill = () => setIsBillOpen(!isBillOpen);
  const { t } = useTranslation();

  return (
    <div className="grid" ref={ref}>
      <div
        className={twMerge(
          "grid grid-cols-[min-content_minmax(150px,1fr)_min-content] items-center gap-x-2 overflow-hidden border-y border-grey-light bg-base-white md:grid-cols-[min-content_minmax(150px,2fr)_min-content_1fr] md:rounded-2xl md:border md:shadow-sm",
          isSummaryMode && "border-y-0 md:border-0 md:shadow-none",
          isCurrentDragging && "bg-blue-hover",
        )}
      >
        <div className="contents">
          {!isSummaryMode ? (
            <div className="mt-3 flex self-baseline pl-6 pr-[11px] md:mt-0 md:self-center md:pr-2">
              <div ref={handleRef}>
                <FeatureFlag flag={"isJointBillServiceDragAndDropEnabled"}>
                  <SixDotsIcon className="size-5" />
                </FeatureFlag>
              </div>
              <IconButton
                icon={ChevronIcon}
                onClick={toggleBill}
                iconClassName={twMerge(
                  "h-5 w-5 text-base-black",
                  isBillOpen && "rotate-180",
                )}
                aria-label={isBillOpen ? t("action.close") : t("action.open")}
              />
            </div>
          ) : (
            <div className="pr-2" />
          )}
          <div className={"py-1.5 md:pb-3 md:pt-5"}>{headerNameCell}</div>
          <div className="hidden flex-col gap-2 md:flex">
            {headerStatusCell}
          </div>
          <div className="ml-auto mt-3 self-baseline pr-4 md:mt-0 md:self-center md:pr-6">
            {headerTotalCell}
          </div>
          {!isNullOrUndefined(mobileTotals) && (
            <div className="col-span-3 mb-3 w-full px-4 pl-[55px] md:hidden">
              <div className="pl-2">{mobileTotals}</div>
            </div>
          )}
          {!isNullOrUndefined(headerNotification) && (
            <div className="col-span-3 mb-3 w-full px-4 md:col-span-4 md:px-6">
              {headerNotification}
            </div>
          )}
          {isBillOpen && !isDragging && (
            <div className="col-span-3 h-px w-full bg-grey-light md:col-span-4" />
          )}
        </div>
        {isBillOpen && !isDragging && (
          <>
            {!isEmpty ? (
              <>
                {children}
                {canBePaid && (
                  <div className="contents">
                    <div className="col-span-3 ml-auto px-4 py-3 md:col-span-4 md:px-6 md:py-5">
                      {footerTotalCell}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="col-span-3 md:col-span-4">{emptyStateCell}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
